/**
 * InternalsContext — "show me how the sausage is made".
 *
 * Every working screen used to advertise which model ran each stage, how full
 * the context window was, what the run cost, and in a few places the provider's
 * raw exception text. That was right when the only user was the person tuning
 * the pipeline. For a customer it is noise, and a Brainstorms buyer learning
 * their good idea came off a 31B local model for $0.14 is a trust problem, not a
 * feature.
 *
 * So internals are OFF for everyone, and an admin flips them back on with
 * Ctrl+Shift+Alt (see ./internalsChord for why a chord and why a toggle). A
 * chord rather than a settings switch because the affordance itself is the thing
 * being removed from the product — a "show internals" checkbox in Settings is
 * the question we are trying to stop customers asking.
 *
 * ONE listener for the whole app, not one per chip. There are ~25 gated sites
 * and they all read this state.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { ChordState, initialChordState, reduceChord } from './internalsChord';
import { clearPref, readPref, writePref } from '../services/crossShellPrefs';

// Client-only, one key — no backend pref, the same as the theme. Shared across
// the product shells via `crossShellPrefs`: localStorage is origin-scoped, so
// an admin who revealed internals on one shell had to type the chord again on
// every other one.
const STORAGE_KEY = 'jcseo_show_internals';

interface InternalsContextType {
  /**
   * The one question every gated site asks. Resolved by the provider — which is
   * mounted inside AuthProvider and so can read the session safely — rather than
   * by each consumer, so a component rendered in isolation never has to know
   * that internals have anything to do with auth.
   */
  showInternals: boolean;
  /** Whether the chord has been toggled on. NOT the same as "may see them". */
  revealed: boolean;
  setRevealed: (v: boolean) => void;
  toggleRevealed: () => void;
}

/**
 * Defaults to HIDDEN rather than null, and nothing here throws when the provider
 * is missing.
 *
 * That is deliberate and it is not the ThemeContext pattern. Theme throws
 * because a subtree with no theme is a bug you want loudly. This gate is read by
 * ~15 components whose only job is to hide things, and its safe direction is
 * "hidden" — so a subtree mounted outside the provider (a test rendering one
 * panel, a future shell that forgets to wrap) should quietly show the customer
 * view, not crash the screen. Failing closed IS the correct behaviour here.
 */
const InternalsContext = createContext<InternalsContextType>({
  showInternals: false,
  revealed: false,
  setRevealed: () => {},
  toggleRevealed: () => {},
});

function initialRevealed(): boolean {
  // `readPref` handles blocked cookies and a throwing localStorage itself, and
  // returns null when it cannot read either — which lands on false here. That is
  // the safe direction for a flag whose whole job is to hide things.
  return readPref(STORAGE_KEY) === '1';
}

export function InternalsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [revealed, setRevealedState] = useState<boolean>(initialRevealed);

  useEffect(() => {
    try {
      if (revealed) writePref(STORAGE_KEY, '1');
      else clearPref(STORAGE_KEY);
    } catch {
      // best-effort persistence
    }
  }, [revealed]);

  // The chord state is a ref, not state: it changes on every modifier press and
  // nothing renders from it. Putting it in useState would re-render the entire
  // app several times per keystroke.
  const chord = useRef<ChordState>(initialChordState);

  useEffect(() => {
    const step = (event: Parameters<typeof reduceChord>[1]) => {
      const { state, toggle } = reduceChord(chord.current, event);
      chord.current = state;
      if (toggle) setRevealedState(v => !v);
    };

    const onKeyDown = (e: KeyboardEvent) =>
      step({
        type: 'keydown',
        key: e.key,
        ctrl: e.ctrlKey,
        shift: e.shiftKey,
        alt: e.altKey,
      });
    const onKeyUp = (e: KeyboardEvent) => step({ type: 'keyup', key: e.key });
    const onMouseDown = () => step({ type: 'mousedown' });
    const onBlur = () => step({ type: 'blur' });

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const setRevealed = useCallback((v: boolean) => setRevealedState(v), []);
  const toggleRevealed = useCallback(() => setRevealedState(v => !v), []);

  return (
    <InternalsContext.Provider
      value={{
        // The chord is obscurity layered on an actual check, never instead of
        // one: a non-admin sharing the keyboard can flip `revealed` all they
        // like and still see nothing.
        showInternals: !!user?.is_admin && revealed,
        revealed,
        setRevealed,
        toggleRevealed,
      }}
    >
      {children}
    </InternalsContext.Provider>
  );
}

export function useInternals(): InternalsContextType {
  return useContext(InternalsContext);
}

/** May I show internals right now? The only question a gated site should ask. */
export function useShowInternals(): boolean {
  return useContext(InternalsContext).showInternals;
}
